import { apiTenant } from '@api/admin';
import LMSProviders from '@context/AppContext';
import { Container, CssBaseline, makeStyles } from '@material-ui/core';
import { selectedActivityIdState } from '@state/activities';
import { selectedCourseIdState, selectedCourseState } from '@state/courses';
import { selectedGroupIdState, selectedGroupState } from '@state/groups';
import { makeLMSCtx } from '@utils/lmsSsrGenerators';
import 'katex/dist/katex.min.css';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { MutableSnapshot, RecoilRoot, RecoilState } from 'recoil';
import 'suneditor/dist/css/suneditor.min.css';
import '../styles/SunEditor.module.css';
import './base.css';

type AppProps = {
  Component: React.ElementType;
  env: LMSEnv;
  tenant: Tenant;
  subdomain: string;
  query: any;
  pageProps: Record<string, any>;
};

const useStyles = makeStyles(theme => ({
  appContainer: {
    textAlign: 'center',
    padding: 0,
    maxWidth: 'none',
  },
}));

const App = ({ Component, env, tenant, subdomain, query, pageProps }: AppProps): JSX.Element => {
  const classes = useStyles();
  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  const title = tenant?.name || '';

  function initializeState({ set }: MutableSnapshot & initializeStateProps) {
    if (pageProps && pageProps.urlProps) {
      if (pageProps.urlProps.course) {
        set(selectedCourseIdState, pageProps.urlProps.course.id);
        set(selectedCourseState, pageProps.urlProps.course);
      }
      if (pageProps.urlProps.group) {
        set(selectedGroupIdState, pageProps.urlProps.group.id);
        set(selectedGroupState, pageProps.urlProps.group);
      }
      if (pageProps.urlProps.activity) {
        set(selectedActivityIdState, pageProps.urlProps.activity.id);
      }
    }
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
        <script src="https://widget.cloudinary.com/v2.0/global/all.js" type="text/javascript"></script>
      </Head>
      <RecoilRoot {...{ initializeState }}>
        <LMSProviders {...{ env, tenant, subdomain, query }}>
          <CssBaseline />
          <Container className={classes.appContainer}>
            <Component {...pageProps} tenant={tenant} />
          </Container>
        </LMSProviders>
      </RecoilRoot>
    </>
  );
};

App.getInitialProps = async ({ ctx: nextCtx, router }: AppInitialProps) => {
  const lmsCtx = makeLMSCtx(nextCtx);
  const uri = nextCtx.req?.url;

  if (uri && lmsCtx.device.isMobile && !uri.includes('/mobile')) {
    const destination = lmsCtx.tenant.urlMobile + uri;

    nextCtx.res.writeHead(307, { Location: destination });
    nextCtx.res.end();
    return { props: {} };
  }

  try {
    const { query } = router;
    const res = await apiTenant.get(lmsCtx.tenant.subdomain);
    if (uri?.includes('/404')) return { env: lmsCtx.env, tenant: res.data };
    if (!res.ok) {
      nextCtx.res.writeHead(307, { Location: '/404' });
      nextCtx.res.end();
      return {};
    }
    const tenant = res.data;
    return { env: lmsCtx.env, tenant, subdomain: lmsCtx.tenant.subdomain, query };
  } catch {}

  return { tenant: {} };
};

App.displayName = 'App';
export default App;

interface AppInitialProps {
  AppTree: any;
  Component: React.Component;
  router: {
    route: string;
    pathname: string;
    query: {
      [x: string]: string;
    };
    asPath: string;
    basePath: string;
    events: unknown | undefined;
    isFallback: boolean;
  };
  ctx: any;
}

interface initializeStateProps {
  _store: {
    getState: () => storeState;
    replaceState: (replacer: any) => object;
    getGraph: (version: any) => object;
    subscribeToTransactions: () => { release: () => {} };
    addTransactionMetada: () => {};
  };
  getLoadable: (recoilValue: any) => object;
  getPromise: (recoilValue: any) => object;
  getNodes_UNSTABLE: (opt: any) => object;
  getDeps_UNSTABLE: (recoilValue: any) => object;
  getSubscribers_UNSTABLE: ({ key }: any) => object;
  getInfo_UNSTABLE: (recoilValue: any) => object;
  map: (mapper: any) => object;
  asyncMap: (mapper: any) => object;
  set: (recoilState: RecoilState<any>, newValueOrUpdater: any) => object;
  reset: (recoilState: RecoilState<any>) => object;
}

type storeState = object;

{
  /* <link rel="apple-touch-icon" sizes="180x180" href={`/icons/${envName}/apple-touch-icon.png`} />
        <link rel="icon" type="image/png" sizes="32x32" href={`/icons/${envName}/favicon-32x32.png`} />
        <link rel="icon" type="image/png" sizes="16x16" href={`/icons/${envName}/favicon-16x16.png`} />
        <link rel="manifest" href={`/icons/${envName}/site.webmanifest`} /> */
}
